import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { ClientManagerInstanceToken } from '../../interfaces/client-manager.interface';
import { ArrayModel } from '../../models/complex/array.model';
import { CompositeModel } from '../../models/complex/composite.model';
import { MaterialPartSetModel } from '../../models/low-level/material-part-set.model';
import { MATERIAL_SOURCE_CODE } from '../../models/low-level/material-source.model';
import { MaterialModel } from '../../models/low-level/material.model';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { TextModel } from '../../models/primitive/text.model';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { UserMasterService } from '../../services/user-master/user-master.service';
import { assignModelOptions } from '../../utilities/assign-model-options';

export class MaterialPickupModel extends CompositeModel {
    public material: MaterialModel;
    public notification: NotificationIdentifierModel;
    public materialPickupPartSet: ArrayModel<MaterialPartSetModel>;
    public selectedPartSet: MaterialPartSetModel;

    public pickupId: string;
    public deliveryQuantity: number;
    public batchNumber: string;
    public ucrNumber: string;

    private date: TextModel;
    private time: TextModel;
    private matIndicator: TextModel;
    public constructor(
        public notificationIdentifierModel: NotificationIdentifierModel,
        materialModel: MaterialModel,
    ) {
        super('MaterialPickupModel');

        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const userMasterService = injector.get(UserMasterService);
        const clientManager = injector.get(ClientManagerInstanceToken);
        const configService = injector.get(ConfigService);

        if (materialModel) {
            this.material = materialModel;
            this.notification = <NotificationIdentifierModel>this.set(notificationIdentifierModel);
            this.date = <TextModel>(
                this.set(new TextModel('Date', moment().format(ConfigConstants.formatDateNoTimezone)))
            );
            this.time = <TextModel>(
                this.set(new TextModel('Time', moment().format(ConfigConstants.formatXSDTimeDuration)))
            );
            // Curr ently MatIndicator value is only "E" in case of pickup
            this.matIndicator = <TextModel>this.set(new TextModel('MatIndicator', 'E'));

            this.materialPickupPartSet = <ArrayModel<MaterialPartSetModel>>(
                this.set(new ArrayModel<MaterialPartSetModel>('MatPickupPartSet'))
            );
            const materialPartSet = new MaterialPartSetModel(
                '',
                materialModel.materialIdentifier.materialNumber.value,
                materialModel.materialIdentifier.orderNumber.value,
                materialModel.materialIdentifier.orderPosition.value,
                materialModel.quantity.value,
                materialModel.ucrNumber,
            );

            if (clientManager.isNgmP58OrSA()) {
                materialPartSet.awb.value = materialModel.awbDelivery;
            }
            // NGMA A-For Additional materials the awbReturn value is entered for pickup
            if (clientManager.isNgmAA() && materialModel.materialSource.isOther()) {
                materialPartSet.awb.value = materialModel.awbReturn.value;
            }

            this.materialPickupPartSet.addModel(materialPartSet);

            this.selectedPartSet = this.materialPickupPartSet.getModelByIndex(0);
            if (clientManager.isNgmAP0()) {
                modelOptionsLoader.load(this.selectedPartSet.awb, 'MaterialPickup', 'AWB');
            }
            modelOptionsLoader.load(this.selectedPartSet.quantity, 'MaterialPickup', 'Quantity');

            modelOptionsLoader.load(this.selectedPartSet.pickupRfid, 'MaterialPickup', 'UCRNumber');

            this.pickupId = materialModel.materialKey;
            this.deliveryQuantity = materialModel.quantity.value ?? 0;
            this.selectedPartSet.quantity = assignModelOptions(
                this.selectedPartSet.quantity,
                {
                    ...this.selectedPartSet.quantity.options,
                    maxValue: this.deliveryQuantity,
                },
                this.selectedPartSet,
            );

            if (clientManager.isNgmP58() || (clientManager.isNgmAP0() && !configService.getDisplayBatchNumber())) {
                this.batchNumber = materialModel.batchNumberLegacy
                    ? materialModel.batchNumberLegacy
                    : materialModel.batchNumber.value;
                this.ucrNumber = materialModel.ucrNumber;
            }
            // if p ickup occur from the split row then make the quantity disabled
            // we d o not support split pickup of already split child
            if (materialModel.materialSource.sourceCode === MATERIAL_SOURCE_CODE.SPLIT) {
                this.selectedPartSet.quantity = assignModelOptions(
                    this.selectedPartSet.quantity,
                    {
                        ...this.selectedPartSet.quantity.options,
                        readonly: true,
                    },
                    this.selectedPartSet,
                );
            }

            if (this.selectedPartSet.feedbackRating) {
                modelOptionsLoader.load(this.selectedPartSet.feedbackRating, 'MaterialPickup', 'FeedbackRating');
            }
            if (this.selectedPartSet.feedbackComment) {
                modelOptionsLoader.load(this.selectedPartSet.feedbackComment, 'MaterialPickup', 'FeedbackComment');
            }

            if (clientManager.isNgmAP0()) {
                userMasterService.setSalesOrgAndPlantForModel(this);
            }
        }
    }
}
